import { BASE_ACCOUNT_API, authorizedFetch, fetchAPI, getHttpErrorMessage } from '@/services/api';

// ==================== TypeScript Interfaces ====================

export interface GlColumnDef {
    fsCode: string;
    fsNameEn: string;
    glCode: string;
    /** 标准 GL 科目英文名（字典）；展示默认用此字段 */
    nameEn: string;
    /** 标准 GL 科目中文名（字典） */
    nameZh: string;
    columnIndex: number;
    currency: string;
    ppeCategory?: string;
    /** BS-EQ-AL 列是否使用 Excel Row5 Cr 公式（Row5=-Row6GT）；旧模板无公式时为 false */
    alExcelRow5CrFlip?: boolean;
    /** 客户别名英文（Co. GL / Excel Row8）；可隐藏，着色展示 */
    aliasEn?: string;
    /** 客户别名中文（Co. GL / Excel Row9） */
    aliasZh?: string;
}

/**
 * 構建複合業務 key: "fsCode|glCode"
 * 此 key 唯一標識一個 GL Sheet 列，取代 Excel 物理列索引
 */
export function buildColumnKey(def: { fsCode: string; glCode: string }): string {
    return `${def.fsCode}|${def.glCode || ''}`;
}

/**
 * 读取上年金额：优先精确 columnKey，再回退 fsCode|LY（AI / Last Year 占位）。
 * 避免图片识别上年与本年发票因 key 不同而各占一列同名科目。
 */
export function resolvePriorYearAmount(
    colKey: string,
    summaryPriorYear: Record<string, number> | undefined,
): number {
    if (!summaryPriorYear || !colKey) return 0;
    const direct = Number(summaryPriorYear[colKey]);
    if (Number.isFinite(direct) && direct !== 0) return direct;
    const pipe = colKey.indexOf('|');
    if (pipe <= 0) return 0;
    const ly = Number(summaryPriorYear[`${colKey.slice(0, pipe)}|LY`]);
    return Number.isFinite(ly) ? ly : 0;
}

/** AI Last Year 占位列（glCode=LY），不应作为独立 Sheet 列展示 */
export function isLyPlaceholderGlCode(glCode: string | null | undefined): boolean {
    return (glCode || '').toUpperCase() === 'LY';
}

/** 列中文：自身 nameZh 优先，否则用标准科目/报表分类补全。 */
export function resolveColumnZh(
    def: { nameZh?: string },
    fallbackZh?: string,
): string {
    const own = (def.nameZh || '').trim();
    return /[\u3400-\u9fff]/.test(own) ? own : (fallbackZh || '').trim();
}

/** DR/CR 表格展示：英文 + 中文（无科目代码）。 */
export function columnDisplayName(
    def: { nameEn?: string; nameZh?: string },
    fallbackZh?: string,
): string {
    const en = (def.nameEn || '').trim();
    const zh = resolveColumnZh(def, fallbackZh);
    if (zh && zh !== en) return `${en} ${zh}`.trim();
    return en || zh;
}

/** DR/CR 下拉：英文 + 中文 + 科目代码。fallbackZh 用于列上没有中文时的标准科目/报表分类补全。 */
export function columnOptionLabel(
    def: { nameEn?: string; nameZh?: string; glCode?: string },
    fallbackZh?: string,
): string {
    const name = columnDisplayName(def, fallbackZh);
    return def.glCode ? `${name} (${def.glCode})` : name;
}

/**
 * 按 fsCode 前綴匹配 columnValues，返回所有匹配 key 的值之和。
 * 用於解決 columnDefs.glCode 與 columnValues key 中 glCode 部分不一致的問題：
 * 例如 columnDef.glCode="0.0" 但 columnValues key 為 "PL-OI-EXG|R-REV-EXG"
 */
export function sumByFsCode(values: Record<string, number> | undefined, fsCode: string): number {
    if (!values) return 0;
    const prefix = fsCode + '|';
    let total = 0;
    for (const key of Object.keys(values)) {
        if (key.startsWith(prefix)) {
            total += values[key] ?? 0;
        }
    }
    return total;
}

/**
 * Excel Row 6 / Grand Total 显示语义：
 * - BS 列（资产负债表）：期末余额 = 上年 Row 10 + 当期数据行变动
 * - PL 列（损益表）：仅当期数据行变动（GT 公式不含 Row 10）
 */
export function isBalanceSheetFsCode(fsCode: string): boolean {
    return fsCode.startsWith('BS-');
}

/** 按 Excel Row 6 规则合并当期变动与上年汇总，得到与 Excel GT 一致的显示值 */
export function mergeGlRow6Display(movement: number, prior: number, fsCode: string): number {
    return isBalanceSheetFsCode(fsCode) ? movement + prior : movement;
}

export interface GlSheetRowVO {
    invoiceId: string;
    date: string | null;
    description: string | null;
    voucherNo: string | null;
    invoiceNo: string | null;
    invoiceDate: string | null;
    chequeNo: string | null;
    details: string | null;
    price: number | null;
    columnValues: Record<string, number>;
    /** Phase 2 方案D: 行級校驗警告（後端解析時生成） */
    validationWarnings?: string[];
}

export interface GlColumnMetaVO {
    fsCode: string;
    glCode: string;
    currency: string;
    titleEn?: string;
}

export interface GlSheetPageVO {
    list: GlSheetRowVO[];
    totalRow: number;
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    /** 上年 AI 合并 remark：fsCode → 文案 */
    summaryPriorYearRemarks?: Record<string, string>;
    /** Phase 1 方案B: gl-import-cache API 一併返回的列定義（其他接口為 undefined） */
    columnDefs?: GlColumnDef[];
    /** 导入解析后的列元数据（含 Excel Row 8 标题） */
    columnMetaMap?: Record<string, GlColumnMetaVO>;
}

/** Company Info sheet 解析结果 */
export interface CompanyInfoImportData {
    companyName?: string;
    industryRaw?: string;
    /** Multi industry ids (Excel single value → one-element array) */
    industry?: number[] | null;
    periodFrom?: string;
    periodTo?: string;
    lastYearPeriodFrom?: string;
    lastYearPeriodTo?: string;
    inCorpDate?: string;
    registeredOffice?: string;
    br?: string;
    directors?: Array<{
        name: string;
        appointmentDate?: string;
        resignDate?: string;
        idNo?: string;
        address?: string;
    }>;
    shareholders?: Array<{ name: string }>;
    cessation?: number;
    limitByGuarantee?: number;
    changeOfAddress?: number;
    lastAddress?: string;
    qualifyOpinion?: number;
    firstAudit?: number;
    ppe?: number;
    relatedPartyTransactions?: number;
    rentalCommitment?: number;
    holdingCo?: number;
    nameOfImmediate?: string;
    nameOfUltimate?: string;
    allotmentOfShares?: number;
    noOfShares?: number;
    pricePerShare?: number;
    priceCurrency?: string;
    dividendPaid?: number;
    dividendCurrency?: string;
    amountPerEachShare?: number;
    totalDividendAmount?: number;
    dividendPayableBf?: number;
    dividendDeclared?: number;
    dividendPaidAmount?: number;
    formerAuditor?: string;
    addressOfFormerAuditor?: string;
    isChangeName?: number;
    oldCompanyName?: string;
    changeEffectiveDate?: string;
    changeOfFinancialYearEnd?: number;
    dateOfBoardResolution?: string;
    reasonToChange?: string;
    presentationCurrency?: string;
    functionalCurrency?: string;
    taxFileNo?: string;
    taxFileNoRaw?: string;
    twoTierTaxThisYear?: number;
}

/**
 * Column Mapping 列信息（全量：已匹配 + 未匹配）。
 * matchScore=1.0 时预填；未匹配需手选。
 */
export interface UnmatchedColumn {
    columnIndex: number;
    headerTitle: string;
    /** 新格式 Row 9 中文列标题 */
    headerTitleZh?: string;
    /** 新格式 Row 4 fs-code；有则下拉按 FS 筛叶子 */
    fsCode?: string;
    /** 建议 / 已解析的 glCode */
    suggestedGlCode?: string;
    /** 建议 / 已解析的 nameEn */
    suggestedNameEn?: string;
    /** 匹配置信度 (0~1)；精确匹配为 1.0 */
    matchScore?: number;
    /** NAME_EN / NORMALIZED / ALIAS / ROW5 / FUZZY / UNMATCHED */
    matchSource?: string;
    /** 已解析 gl_code（未匹配为 null） */
    glCode?: string | null;
}

/** 与 UnmatchedColumn 同源；后端权威字段名 */
export type GlColumnMapping = UnmatchedColumn;

export interface GlImportResultVO {
    totalRows: number;
    insertedCount: number;
    updatedCount: number;
    errorCount: number;
    errors: string[];
    warnings?: string[];
    /** 解析后的预览数据（仅解析模式下返回，保存模式下为 null） */
    previewData: GlSheetPageVO | null;
    /** Company Info sheet 解析结果 */
    companyInfoData?: CompanyInfoImportData;
    /** 后端匹配到的公司信息 */
    matchedCompany?: any;
    /** BR 是否匹配到已有公司 */
    companyMatched?: boolean;
    /** 公司名称是否已自动更新 */
    companyNameUpdated?: boolean;
    /** GL 预览数据缓存 token（Phase 3），前端可通过此 token 在 StepC 时取回 GL 数据 */
    importToken?: string;
    /** 旧格式标志: true=旧格式 Excel（Row 4 无 fsCode） */
    legacyFormat?: boolean;
    /** 全量 Column Mapping（已匹配 + 未匹配）；有 importToken 时始终展示映射步 */
    columnMappings?: GlColumnMapping[];
    /** 兼容字段：与 columnMappings 同源 */
    unmatchedColumns?: UnmatchedColumn[];
    /** 新格式：Row 4 fs-code 空白或不在 FS 字典中的列提醒 */
    fsCodeWarnings?: string[];
    /** Row 8 列名称未匹配到 gl_code 的提醒 */
    glCodeWarnings?: string[];
    /** 后端权威 key 映射表（confirmLegacyMapping 返回），用于客户端重映射预加载数据的 drCrItems key */
    keyMapping?: Record<string, string>;
    /** Bank statement import: opening/closing/variance per PDF account */
    bankSummaries?: BankAccountSummaryVO[];
    /** Bank statement import: PDF accounts not yet mapped to Co.GL columns */
    unmatchedBankAccounts?: UnmatchedBankAccountVO[];
    /** Bank statement source, e.g. BOC */
    bankType?: string;
    /** Company name extracted from bank statement PDF */
    bankCompanyName?: string;
}

export interface BankAccountSummaryVO {
    accountKey: string;
    accountType?: string;
    currency?: string;
    broughtForwardDate?: string;
    openingBalance?: number;
    closingBalance?: number;
    varianceExcel?: number;
    varianceStatement?: number;
    varianceDiff?: number;
}

export interface UnmatchedBankAccountVO {
    accountKey: string;
    accountType?: string;
    accountNumber?: string;
    currency?: string;
    mappedColumnKey?: string;
}

export interface GlSheetPageRequest {
    companyId: number;
    annualId: number;
    pageNumber: number;
    pageSize: number;
    /** 0 = 不分页（PageEntity 允许 >100 条）；默认 1 时每页最多 100 条 */
    pageType?: number;
    sortBy?: string;
    filterDate?: string;
    filterDesc?: string;
}

export interface InvoiceDetailWithCounterpartVO {
    invoiceId: string;
    date: string | null;
    description: string | null;
    voucherNo: string | null;
    invoiceNo: string | null;
    invoiceDate: string | null;
    chequeNo: string | null;
    details: string | null;
    amount: number | null;
    columnTitle: string | null;
    counterparts: CounterpartEntry[];
}

export interface CounterpartEntry {
    fsCode: string;
    fsName: string;
    columnTitle: string;
    amount: number;
    currency: string;
}

// ==================== API Functions ====================

const INVOICE_URL = BASE_ACCOUNT_API + '/invoice';
const GL_CATEGORY_URL = BASE_ACCOUNT_API + '/gl-category';

/** 获取 GL 列定义（无缓存，每次请求最新标准列定义） */
export async function fetchColumnDefs(): Promise<GlColumnDef[]> {
    return fetchAPI<GlColumnDef[]>(`${GL_CATEGORY_URL}/column-defs`, {
        method: 'GET',
    });
}

/** 分页查询 GL Sheet 数据 */
export async function fetchGlSheetPage(params: GlSheetPageRequest): Promise<GlSheetPageVO> {
    return fetchAPI<GlSheetPageVO>(`${INVOICE_URL}/gl-sheet-page`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
}

/**
 * 拉取 GL Sheet 全量明细行（Tiny Mode / Edit / Submit 校验用）。
 * 后端 PageEntity 在 pageType=1 时每页上限 100，须传 pageType=0 才能一次取全。
 */
export async function fetchGlSheetAllRows(
    companyId: number,
    annualId: number,
    estimatedTotal = 0,
): Promise<GlSheetPageVO> {
    return fetchGlSheetPage({
        companyId,
        annualId,
        pageNumber: 1,
        pageSize: Math.max(estimatedTotal, 10000),
        pageType: 0,
    });
}

/** 导入 GL Excel */
export async function importGlExcel(
    file: File,
    companyId: number,
    annualId: number,
): Promise<GlImportResultVO> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', String(companyId));
    formData.append('annualId', String(annualId));

    // Must use authorizedFetch so path is ciphered when payload cipher is on.
    // FormData stays multipart (backend CryptoPayloadFilter skips multipart).
    const res = await authorizedFetch(`${INVOICE_URL}/import`, {
        method: 'POST',
        body: formData,
    });

    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Import failed'));
    }
    const result = await res.json();
    if (result.code !== 0) {
        throw new Error(result.msg || 'Import error');
    }
    return result.data;
}

/** 导入银行月结单 PDF（第一期仅 BOC；支持一次多个文件） */
export async function importBankStatement(
    fileOrFiles: File | File[],
    companyId: number,
    annualId: number,
    bankType: string = 'BOC',
): Promise<GlImportResultVO> {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    if (files.length === 0) {
        throw new Error('PDF file is required');
    }
    const formData = new FormData();
    for (const f of files) {
        formData.append('file', f);
    }
    formData.append('companyId', String(companyId));
    formData.append('annualId', String(annualId));
    formData.append('bankType', bankType);

    const res = await authorizedFetch(`${INVOICE_URL}/import-bank-statement`, {
        method: 'POST',
        body: formData,
    });

    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Bank statement import failed'));
    }
    const result = await res.json();
    if (result.code !== 0) {
        throw new Error(result.msg || 'Bank statement import error');
    }
    return result.data;
}

/** 确认 PDF 账户 → GL 列映射并刷新预览 */
export async function confirmBankAccountMapping(params: {
    importToken: string;
    companyId: number;
    annualId: number;
    mappings: Array<{ accountKey: string; columnKey: string }>;
}): Promise<GlImportResultVO> {
    return fetchAPI<GlImportResultVO>(`${INVOICE_URL}/confirm-bank-account-mapping`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
}

/** 导出 GL Excel — fetch+blob 方式，携带 auth token */
export async function exportGlExcel(companyId: number, annualId: number) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });

    const res = await authorizedFetch(`${INVOICE_URL}/export?${params.toString()}`, {
        method: 'GET',
    });

    if (!res.ok) {
        throw new Error('Export failed: ' + res.statusText);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?([^;\s]+)/i);
    const filename = filenameMatch
        ? decodeURIComponent(filenameMatch[1].replace(/['"]/g, ''))
        : `GL_Export_${companyId}_${annualId}.xlsm`;

    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    a.style.display = 'none';    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
}

/** 查询指定 fs-code 下的 invoice 明细及对方科目 */
export async function fetchDetailByFsCode(
    companyId: number,
    annualId: number,
    fsCode: string,
): Promise<InvoiceDetailWithCounterpartVO[]> {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
        fsCode,
    });
    return fetchAPI<InvoiceDetailWithCounterpartVO[]>(
        `${INVOICE_URL}/detail-by-fs-code?${params.toString()}`,
        { method: 'GET' },
    );
}

/** 手动录入多条 invoice 并批量保存（复用 Excel 导入结构） */
export async function saveGlPreview(params: {
    companyId: number;
    annualId: number;
    rows: GlSheetRowVO[];
    summaryPriorYear?: Record<string, number>;
    summaryPriorYearRemarks?: Record<string, string>;
    columnMetaMap?: Record<string, GlColumnMetaVO>;
    deletedInvoiceIds?: string[];
    /** true：按业务指纹匹配已有行 upsert（相同跳过 / 不同更新） */
    replaceExisting?: boolean;
    /** replaceExisting 时是否软删未匹配旧行；银行 PDF 增量导入应传 false */
    softDeleteUnmatched?: boolean;
}): Promise<GlImportResultVO> {
    return fetchAPI<GlImportResultVO>(`${INVOICE_URL}/save-gl-preview`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
}

/** 根据 importToken 从 Redis 取回 GL 预览数据（Phase 3 缓存），companyId 用于别名替换 */
export async function fetchGlImportCache(importToken: string, companyId?: number): Promise<GlSheetPageVO | null> {
    const params = new URLSearchParams({ importToken });
    if (companyId != null) params.set('companyId', String(companyId));
    return fetchAPI<GlSheetPageVO | null>(`${INVOICE_URL}/gl-import-cache?${params.toString()}`, {
        method: 'GET',
    });
}

/** [OPT-2] 确认 GL Sheet 列映射请求（旧格式 title 未命中 / 新格式 FS→GL） */
export interface ConfirmLegacyMappingRequest {
    importToken: string;
    companyId: number;
    mappings: Array<{
        columnIndex: number;
        headerTitle: string;
        /**
         * 用户选择的 GL Category code（对应 account_gl_category.code）。
         * 字段原名 glCode，已与 account_company_gl_category.code 统一命名。
         */
        code: string;
        /** 用户选择的 GL Category 标准英文名，供后端与 headerTitle 比较判断是否需保存别名 */
        selectedNameEn?: string;
        /** 新格式 Row 9 中文标题 → Co.GL aliasZh */
        aliasZh?: string;
    }>;
    /** true 时后端自动根据 account_company_gl_category 别名补全映射，忽略 mappings 列表 */
    autoResolveFromAlias?: boolean;
}

/** [OPT-2] 确认旧格式 GL Sheet 列映射: 保存别名 + 返回补充后的完整 previewData */
export async function confirmLegacyMapping(
    params: ConfirmLegacyMappingRequest,
): Promise<GlImportResultVO> {
    return fetchAPI<GlImportResultVO>(`${INVOICE_URL}/confirm-legacy-mapping`, {
        method: 'POST',
        body: JSON.stringify(params),
    });
}

/** 检查别名是否全部存在（用于映射步骤 Skip 判断） */
export async function checkAliasExists(
    companyId: number,
    headerTitles: string[],
): Promise<{ allExist: boolean; missingTitles: string[] }> {
    return fetchAPI<{ allExist: boolean; missingTitles: string[] }>(
        `${INVOICE_URL}/check-alias-exists`,
        {
            method: 'POST',
            body: JSON.stringify({ companyId, headerTitles }),
        },
    );
}

/** 根据 description + details 推荐科目 */
export async function suggestItemCodes(
    companyId: number,
    description: string,
    details: string,
) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        description,
        details,
    });
    return fetchAPI(`${INVOICE_URL}/suggest?${params.toString()}`, { method: 'GET' });
}

// ==================== GL Import History (audit trail) ====================

export interface GlImportBatchVO {
    batchId: number;
    companyId: number;
    annualId: number;
    operationSource?: string;
    replaceExisting?: number;
    prevInvoiceCount?: number;
    prevDetailCount?: number;
    newInsertedCount?: number | null;
    newUpdatedCount?: number | null;
    newErrorCount?: number | null;
    changeTime?: string;
    changeUserId?: number | null;
    operationIp?: string | null;
}

export interface GlImportInvoiceSnap {
    historyId: number;
    invoiceId: string;
    changeType?: string | null;
    snapKind?: string | null;
    date?: string | null;
    description?: string | null;
    voucherNo?: string | null;
    invoiceNo?: string | null;
    invoiceDate?: string | null;
    chequeNo?: string | null;
    details?: string | null;
    price?: number | null;
}

export interface GlImportAmountDetailSnap {
    historyId: number;
    invoiceId: string;
    changeType?: string | null;
    snapKind?: string | null;
    detailId?: string | null;
    fsCode?: string | null;
    glCode?: string | null;
    amount?: number | null;
    currency?: string | null;
}

export interface GlImportInvoiceChange {
    changeType: string;
    invoiceId: string;
    before?: GlImportInvoiceSnap | null;
    after?: GlImportInvoiceSnap | null;
    beforeDetails?: GlImportAmountDetailSnap[];
    afterDetails?: GlImportAmountDetailSnap[];
}

export interface GlImportBatchDetailVO {
    batch: GlImportBatchVO;
    /** 按变更分组的 before/after */
    changes?: GlImportInvoiceChange[];
    /** 兼容：仅 BEFORE 扁平列表 */
    invoices?: GlImportInvoiceSnap[];
    /** 兼容：扁平金额明细 */
    amountDetails?: GlImportAmountDetailSnap[];
}

/** 列出公司+年度的 GL Excel 覆盖导入批次 */
export async function fetchGlImportHistory(
    companyId: number,
    annualId: number,
): Promise<GlImportBatchVO[]> {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    return fetchAPI<GlImportBatchVO[]>(`${INVOICE_URL}/gl-import-history?${params}`, {
        method: 'GET',
    });
}

/** 批次头 + 变更快照 invoices + amountDetails */
export async function fetchGlImportHistoryDetail(
    batchId: number,
): Promise<GlImportBatchDetailVO> {
    return fetchAPI<GlImportBatchDetailVO>(`${INVOICE_URL}/gl-import-history/${batchId}`, {
        method: 'GET',
    });
}

// ==================== Tiny Mode transfer ====================

export interface GlInvoiceTransferHistoryVO {
    batchId: number;
    changeTime?: string | null;
    changeUserId?: number | null;
    invoiceId: string;
    /** TINY_TRANSFER / TINY_TRANSFER_REVERT */
    operationSource?: string | null;
    description?: string | null;
    date?: string | null;
    targetFsCode?: string | null;
    targetGlCode?: string | null;
    beforeDetails?: GlImportAmountDetailSnap[];
    afterDetails?: GlImportAmountDetailSnap[];
}

export interface GlInvoiceTransferRequest {
    companyId: number;
    annualId: number;
    invoiceId: string;
    targetFsCode: string;
    targetGlCode?: string | null;
    /** 当前 Tiny Tab 源科目；有值时只从该科目扣减，余额留源 */
    sourceFsCode?: string | null;
    sourceGlCode?: string | null;
    /** 转移金额绝对值（正数）；不传则转源科目全额 */
    transferAmount?: number | null;
}

export interface GlInvoiceTransferBatchItem {
    invoiceId: string;
    transferAmount?: number | null;
}

export interface GlInvoiceTransferBatchRequest {
    companyId: number;
    annualId: number;
    targetFsCode: string;
    targetGlCode?: string | null;
    sourceFsCode?: string | null;
    sourceGlCode?: string | null;
    items: GlInvoiceTransferBatchItem[];
}

/** Tiny Mode：源科目（可部分）金额转移到目标 GL 列 */
export async function transferGlInvoiceCategory(
    body: GlInvoiceTransferRequest,
): Promise<GlInvoiceTransferHistoryVO> {
    return fetchAPI<GlInvoiceTransferHistoryVO>(`${INVOICE_URL}/transfer-category`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/** Tiny Mode：批量转移（同一源/目标；末项可 partial） */
export async function transferGlInvoiceCategoryBatch(
    body: GlInvoiceTransferBatchRequest,
): Promise<GlInvoiceTransferHistoryVO[]> {
    return fetchAPI<GlInvoiceTransferHistoryVO[]>(`${INVOICE_URL}/transfer-category-batch`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

export interface GlInvoiceTransferRevertRequest {
    companyId: number;
    annualId: number;
    batchId: number;
    /** 空 = 整批还原 */
    invoiceId?: string | null;
}

/** Tiny Mode：原路返回（单票或整批） */
export async function revertGlInvoiceTransfer(
    body: GlInvoiceTransferRevertRequest,
): Promise<GlInvoiceTransferHistoryVO[]> {
    return fetchAPI<GlInvoiceTransferHistoryVO[]>(`${INVOICE_URL}/transfer-revert`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

/**
 * Tiny Mode：转移留痕。
 * 不传 invoiceId 时返回该公司年度全部转移记录。
 */
export async function fetchInvoiceTransferHistory(
    companyId: number,
    annualId: number,
    invoiceId?: string,
): Promise<GlInvoiceTransferHistoryVO[]> {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });
    if (invoiceId) params.set('invoiceId', invoiceId);
    return fetchAPI<GlInvoiceTransferHistoryVO[]>(`${INVOICE_URL}/transfer-history?${params}`, {
        method: 'GET',
    });
}

/** 上年 BS/PL 抽取匹配行 */
export interface PriorYearFsMatchVO {
    sourceLabel?: string;
    sourceAmount?: number;
    fsCode?: string;
    fsNameEn?: string;
    /** 公司 alias 建议的 GL code */
    suggestedGlCode?: string | null;
    confidence?: string;
    confidencePercent?: number;
    status?: string;
    reason?: string;
    priorRawAmount?: number;
}

/** 上年 BS/PL 抽取结果（可灌入 summaryPriorYear） */
export interface PriorYearFsExtractResultVO {
    summaryPriorYear: Record<string, number>;
    matches: PriorYearFsMatchVO[];
    warnings: string[];
    filledCount: number;
    unmatchedCount: number;
    /** 源报表识别到的期间文案 */
    detectedPeriodLabel?: string | null;
    detectedAsAtDate?: string | null;
    detectedPeriodFrom?: string | null;
    detectedPeriodTo?: string | null;
}

export interface PriorYearFsAliasSaveItem {
    sourceLabel: string;
    fsCode: string;
    glCode?: string | null;
}

/**
 * 上传上年 BS/PL（图/PDF/Excel），抽取科目金额并匹配 FS code。
 * 返回的 summaryPriorYear key 默认为 fsCode|LY；前端 Apply 可改写为 fsCode|glCode。
 */
export async function extractPriorYearFs(
    files: File[],
    companyId?: number | null,
): Promise<PriorYearFsExtractResultVO> {
    if (!files?.length) throw new Error('Please select at least one file');
    const formData = new FormData();
    for (const f of files) {
        formData.append('files', f);
    }
    if (companyId != null && Number.isFinite(companyId) && companyId > 0) {
        formData.append('companyId', String(companyId));
    }
    const res = await authorizedFetch(`${INVOICE_URL}/prior-year/extract-fs`, {
        method: 'POST',
        body: formData,
    });
    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'Extract failed'));
    }
    const result = await res.json();
    if (result.code !== 0) {
        throw new Error(result.msg || 'Extract error');
    }
    return result.data as PriorYearFsExtractResultVO;
}

/** 将 preview 勾选行的 Source 写入 Co.GL alias（company_gl_category） */
export async function savePriorYearFsAliases(
    companyId: number,
    items: PriorYearFsAliasSaveItem[],
): Promise<number> {
    if (!companyId) throw new Error('companyId required');
    if (!items?.length) return 0;
    return fetchAPI<number>(`${INVOICE_URL}/prior-year/fs-aliases`, {
        method: 'POST',
        body: JSON.stringify({ companyId, items }),
    }) ?? 0;
}
